import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './MediaPage.css';

// EMBY Configuration
const EMBY_CONFIG = {
  serverUrl: 'https://media.allstarmediapro.com',
  apiKey: '8cf23d5e47174b8bae3b58e5eb3e6718',
};

// TMDB Configuration (free API for movie metadata)
const TMDB_CONFIG = {
  apiKey: '2a15ecc7f92a4238d3cb4b1cc8e90143', // Free TMDB API key
  baseUrl: 'https://api.themoviedb.org/3',
  imageBaseUrl: 'https://image.tmdb.org/t/p',
};

// Cache for TMDB lookups to avoid repeated API calls
const tmdbCache: Record<string, any> = {};

// Search TMDB for a movie by title
const searchTMDB = async (title: string, year?: number): Promise<any | null> => {
  const cacheKey = `${title}-${year || ''}`;
  if (tmdbCache[cacheKey]) return tmdbCache[cacheKey];

  try {
    const yearParam = year ? `&year=${year}` : '';
    const searchUrl = `${TMDB_CONFIG.baseUrl}/search/movie?api_key=${TMDB_CONFIG.apiKey}&query=${encodeURIComponent(title)}${yearParam}`;
    const res = await fetch(searchUrl);
    if (res.ok) {
      const data = await res.json();
      if (data.results && data.results.length > 0) {
        const movie = data.results[0];
        // Fetch full details including credits
        const detailsUrl = `${TMDB_CONFIG.baseUrl}/movie/${movie.id}?api_key=${TMDB_CONFIG.apiKey}&append_to_response=credits`;
        const detailsRes = await fetch(detailsUrl);
        if (detailsRes.ok) {
          const details = await detailsRes.json();
          tmdbCache[cacheKey] = details;
          return details;
        }
        tmdbCache[cacheKey] = movie;
        return movie;
      }
    }
  } catch (err) {
    console.error('TMDB search error:', err);
  }
  return null;
};

// Get TMDB image URL
const getTMDBImage = (path: string | null, size: 'w200' | 'w400' | 'w500' | 'w780' | 'original' = 'w400'): string => {
  if (!path) return '';
  return `${TMDB_CONFIG.imageBaseUrl}/${size}${path}`;
};

// Enrich MediaItem with TMDB data
const enrichWithTMDB = async (item: MediaItem): Promise<MediaItem> => {
  if (item.rating && item.actors && item.actors.length > 0) {
    return item; // Already has metadata
  }

  const tmdbData = await searchTMDB(item.title, item.year);
  if (!tmdbData) return item;

  const cast = tmdbData.credits?.cast?.slice(0, 5).map((c: any) => c.name) || [];
  const director = tmdbData.credits?.crew?.find((c: any) => c.job === 'Director')?.name;

  return {
    ...item,
    rating: item.rating || (tmdbData.vote_average ? Math.round(tmdbData.vote_average * 10) / 10 : undefined),
    synopsis: item.synopsis || tmdbData.overview,
    genres: item.genres.length > 0 ? item.genres : (tmdbData.genres?.map((g: any) => g.name) || []),
    actors: item.actors && item.actors.length > 0 ? item.actors : cast,
    director: item.director || director,
    year: item.year || (tmdbData.release_date ? parseInt(tmdbData.release_date.split('-')[0]) : undefined),
    imageUrl: item.imageUrl || getTMDBImage(tmdbData.poster_path, 'w400'),
    backdropUrl: item.backdropUrl || getTMDBImage(tmdbData.backdrop_path, 'w780'),
    tagline: item.tagline || tmdbData.tagline,
    tmdbId: item.tmdbId || tmdbData.id?.toString(),
  };
};

// Types
interface MediaItem {
  id: string;
  title: string;
  year?: number;
  rating?: number;
  criticRating?: number; // Rotten Tomatoes
  runtime?: string;
  genres: string[];
  synopsis?: string;
  imageUrl?: string;
  backdropUrl?: string;
  progress?: number;
  episode?: string;
  type: 'movie' | 'series';
  actors?: string[];
  director?: string;
  studio?: string;
  imdbId?: string;
  tmdbId?: string;
  officialRating?: string; // PG, R, etc
  tagline?: string;
}

// Icons
const Icons = {
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  discover: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  watchlist: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>,
  downloads: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  play: <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

// Helper: Get EMBY image URL
const getEmbyImage = (itemId: string, imageTag: string | undefined, type: 'Primary' | 'Backdrop' = 'Primary', width = 400): string => {
  if (!imageTag) return '';
  return `${EMBY_CONFIG.serverUrl}/emby/Items/${itemId}/Images/${type}?maxWidth=${width}&tag=${imageTag}&api_key=${EMBY_CONFIG.apiKey}`;
};

// Helper: Convert EMBY item to MediaItem
const embyToMediaItem = (item: any): MediaItem => {
  const imageTag = item.ImageTags?.Primary;
  const backdropTag = item.BackdropImageTags?.[0];
  const actors = item.People?.filter((p: any) => p.Type === 'Actor').slice(0, 5).map((p: any) => p.Name) || [];
  const director = item.People?.find((p: any) => p.Type === 'Director')?.Name;

  // Extract title - remove prefix patterns like "01 Movie Name - Genre"
  let title = item.Name || 'Unknown';
  const prefixMatch = title.match(/^\d+\s+(.+?)(?:\s+-\s+\w+)?$/);
  if (prefixMatch) title = prefixMatch[1];

  return {
    id: item.Id,
    title,
    year: item.ProductionYear,
    rating: item.CommunityRating ? Math.round(item.CommunityRating * 10) / 10 : undefined,
    criticRating: item.CriticRating, // Rotten Tomatoes score
    runtime: item.RunTimeTicks ? `${Math.round(item.RunTimeTicks / 600000000)}m` : undefined,
    genres: item.Genres || [],
    synopsis: item.Overview || item.Taglines?.[0],
    imageUrl: imageTag ? getEmbyImage(item.Id, imageTag, 'Primary', 400) : undefined,
    backdropUrl: backdropTag ? getEmbyImage(item.Id, backdropTag, 'Backdrop', 1920) : undefined,
    progress: item.UserData?.PlayedPercentage || 0,
    type: item.Type === 'Movie' ? 'movie' : 'series',
    actors,
    director,
    studio: item.Studios?.[0]?.Name,
    imdbId: item.ProviderIds?.Imdb,
    tmdbId: item.ProviderIds?.Tmdb,
    officialRating: item.OfficialRating,
    tagline: item.Taglines?.[0],
  };
};

// Expanded genre list - comprehensive coverage
const ALL_GENRES = [
  'All', 'Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary',
  'Drama', 'Family', 'Fantasy', 'History', 'Horror', 'Music', 'Mystery',
  'Romance', 'Sci-Fi', 'Science Fiction', 'Thriller', 'War', 'Western'
];

// Duration filter options (in minutes)
const DURATION_FILTERS = [
  { label: 'Any Length', min: 0, max: Infinity },
  { label: 'Under 90 min', min: 0, max: 90 },
  { label: '90-120 min', min: 90, max: 120 },
  { label: '120-150 min', min: 120, max: 150 },
  { label: 'Over 150 min', min: 150, max: Infinity },
  { label: 'Under 30 min', min: 0, max: 30 },
];

// Year/Decade filter options
const YEAR_FILTERS = [
  { label: 'Any Year', min: 0, max: 9999 },
  { label: '2020s', min: 2020, max: 2029 },
  { label: '2010s', min: 2010, max: 2019 },
  { label: '2000s', min: 2000, max: 2009 },
  { label: '1990s', min: 1990, max: 1999 },
  { label: '1980s', min: 1980, max: 1989 },
  { label: 'Pre-1980', min: 0, max: 1979 },
];

// Rating filter (MPAA/TV ratings)
const RATING_FILTERS = [
  { label: 'Any Rating', values: [] as string[] },
  { label: 'G / TV-G', values: ['G', 'TV-G'] },
  { label: 'PG / TV-PG', values: ['PG', 'TV-PG'] },
  { label: 'PG-13 / TV-14', values: ['PG-13', 'TV-14'] },
  { label: 'R / TV-MA', values: ['R', 'TV-MA', 'NC-17'] },
  { label: 'Unrated', values: ['NR', 'Unrated', ''] },
];

// Sort options
const SORT_OPTIONS = [
  { label: 'Recently Added', key: 'dateAdded', order: 'desc' as const },
  { label: 'Title A-Z', key: 'title', order: 'asc' as const },
  { label: 'Title Z-A', key: 'title', order: 'desc' as const },
  { label: 'Year (Newest)', key: 'year', order: 'desc' as const },
  { label: 'Year (Oldest)', key: 'year', order: 'asc' as const },
  { label: 'Rating (Highest)', key: 'rating', order: 'desc' as const },
  { label: 'Rating (Lowest)', key: 'rating', order: 'asc' as const },
];

// Filter state interface
interface FilterState {
  genres: string[];
  duration: typeof DURATION_FILTERS[0];
  year: typeof YEAR_FILTERS[0];
  rating: typeof RATING_FILTERS[0];
  contentType: 'all' | 'movie' | 'series';
  minRating: number;
  actors: string[];
  sort: typeof SORT_OPTIONS[0];
}

const defaultFilters: FilterState = {
  genres: [],
  duration: DURATION_FILTERS[0],
  year: YEAR_FILTERS[0],
  rating: RATING_FILTERS[0],
  contentType: 'all',
  minRating: 0,
  actors: [],
  sort: SORT_OPTIONS[0],
};

// Helper to extract runtime in minutes
const getRuntimeMinutes = (runtime: string | undefined): number => {
  if (!runtime) return 0;
  const match = runtime.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Filter icons
const FilterIcons = {
  filter: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  star: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  film: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>,
  sort: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="8" y2="18"/></svg>,
  chevronDown: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  reset: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-8.38"/></svg>,
};

// ========== COMPONENTS ==========

const Sidebar: React.FC<{ activeNav: string; onNavChange: (id: string) => void }> = ({ activeNav, onNavChange }) => (
  <aside className="orama-sidebar">
    <div className="orama-sidebar__logo">
      <span className="orama-sidebar__logo-dot" />
      <span className="orama-sidebar__logo-text">Orama</span>
    </div>
    <nav className="orama-sidebar__nav">
      {[
        { id: 'home', label: 'Home', icon: Icons.home },
        { id: 'discover', label: 'Discover', icon: Icons.discover },
        { id: 'watchlist', label: 'Watchlist', icon: Icons.watchlist },
        { id: 'downloads', label: 'Downloads', icon: Icons.downloads },
      ].map(item => (
        <button
          key={item.id}
          className={`orama-sidebar__item${activeNav === item.id ? ' orama-sidebar__item--active' : ''}`}
          onClick={() => onNavChange(item.id)}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
    <div className="orama-sidebar__bottom">
      <button className="orama-sidebar__item">{Icons.home}<span>Home</span></button>
      <button className="orama-sidebar__item">{Icons.downloads}<span>Downloads</span></button>
    </div>
  </aside>
);

// Advanced Filter Panel Component
const FilterPanel: React.FC<{
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  allActors: string[];
  onReset: () => void;
  activeFiltersCount: number;
}> = ({ filters, onFiltersChange, allActors, onReset, activeFiltersCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [actorSearch, setActorSearch] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const filteredActors = actorSearch
    ? allActors.filter(a => a.toLowerCase().includes(actorSearch.toLowerCase())).slice(0, 20)
    : allActors.slice(0, 20);

  const toggleGenre = (genre: string) => {
    const newGenres = filters.genres.includes(genre)
      ? filters.genres.filter(g => g !== genre)
      : [...filters.genres, genre];
    onFiltersChange({ ...filters, genres: newGenres });
  };

  return (
    <div className="filter-panel">
      <div className="filter-panel__header">
        <button className={`filter-panel__toggle ${isOpen ? 'active' : ''} ${activeFiltersCount > 0 ? 'has-filters' : ''}`} onClick={() => setIsOpen(!isOpen)}>
          {FilterIcons.filter}
          <span>Filters</span>
          {activeFiltersCount > 0 && <span className="filter-panel__badge">{activeFiltersCount}</span>}
          {FilterIcons.chevronDown}
        </button>
        {activeFiltersCount > 0 && (
          <button className="filter-panel__reset" onClick={onReset} title="Reset all filters">
            {FilterIcons.reset}
            <span>Reset</span>
          </button>
        )}
      </div>

      {isOpen && (
        <div className="filter-panel__content">
          {/* Genre Multi-Select */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.film} Genres</div>
            <div className="filter-chips">
              {ALL_GENRES.filter(g => g !== 'All').map(genre => (
                <button
                  key={genre}
                  className={`filter-chip ${filters.genres.includes(genre) ? 'active' : ''}`}
                  onClick={() => toggleGenre(genre)}
                >
                  {filters.genres.includes(genre) && FilterIcons.check}
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Content Type */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.film} Content Type</div>
            <div className="filter-buttons">
              {(['all', 'movie', 'series'] as const).map(type => (
                <button
                  key={type}
                  className={`filter-btn ${filters.contentType === type ? 'active' : ''}`}
                  onClick={() => onFiltersChange({ ...filters, contentType: type })}
                >
                  {type === 'all' ? 'All' : type === 'movie' ? 'Movies' : 'TV Shows'}
                </button>
              ))}
            </div>
          </div>

          {/* Duration Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.clock} Duration</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'duration' ? null : 'duration')}>
              <span>{filters.duration.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'duration' && (
                <div className="filter-dropdown__menu">
                  {DURATION_FILTERS.map((opt, i) => (
                    <button key={i} className={filters.duration.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, duration: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Year Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.calendar} Release Year</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'year' ? null : 'year')}>
              <span>{filters.year.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'year' && (
                <div className="filter-dropdown__menu">
                  {YEAR_FILTERS.map((opt, i) => (
                    <button key={i} className={filters.year.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, year: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* MPAA Rating Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.star} Age Rating</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'rating' ? null : 'rating')}>
              <span>{filters.rating.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'rating' && (
                <div className="filter-dropdown__menu">
                  {RATING_FILTERS.map((opt, i) => (
                    <button key={i} className={filters.rating.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, rating: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Minimum Score Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.star} Minimum Score</div>
            <div className="filter-slider">
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={filters.minRating}
                onChange={(e) => onFiltersChange({ ...filters, minRating: parseFloat(e.target.value) })}
              />
              <span className="filter-slider__value">{filters.minRating > 0 ? `${filters.minRating}+` : 'Any'}</span>
            </div>
          </div>

          {/* Actor Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.user} Actors</div>
            <div className="filter-actor-search">
              <input
                type="text"
                placeholder="Search actors..."
                value={actorSearch}
                onChange={(e) => setActorSearch(e.target.value)}
              />
              {filters.actors.length > 0 && (
                <div className="filter-actor-selected">
                  {filters.actors.map(actor => (
                    <span key={actor} className="filter-actor-tag">
                      {actor}
                      <button onClick={() => onFiltersChange({ ...filters, actors: filters.actors.filter(a => a !== actor) })}>×</button>
                    </span>
                  ))}
                </div>
              )}
              {actorSearch && (
                <div className="filter-actor-suggestions">
                  {filteredActors.map(actor => (
                    <button
                      key={actor}
                      className={filters.actors.includes(actor) ? 'active' : ''}
                      onClick={() => {
                        if (!filters.actors.includes(actor)) {
                          onFiltersChange({ ...filters, actors: [...filters.actors, actor] });
                        }
                        setActorSearch('');
                      }}
                    >
                      {actor}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sort Options */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.sort} Sort By</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'sort' ? null : 'sort')}>
              <span>{filters.sort.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'sort' && (
                <div className="filter-dropdown__menu">
                  {SORT_OPTIONS.map((opt, i) => (
                    <button key={i} className={filters.sort.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, sort: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Header with genre quick-filters
const Header: React.FC<{
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onSearchClick: () => void;
  allActors: string[];
  onResetFilters: () => void;
  activeFiltersCount: number;
}> = ({ filters, onFiltersChange, onSearchClick, allActors, onResetFilters, activeFiltersCount }) => {
  // Quick genre selection (first 8 genres for the header bar)
  const quickGenres = ALL_GENRES.slice(0, 10);
  const selectedQuickGenre = filters.genres.length === 1 ? filters.genres[0] : (filters.genres.length === 0 ? 'All' : 'Multi');

  const handleQuickGenreClick = (genre: string) => {
    if (genre === 'All') {
      onFiltersChange({ ...filters, genres: [] });
    } else {
      onFiltersChange({ ...filters, genres: [genre] });
    }
  };

  return (
    <header className="orama-header">
      <div className="orama-genres">
        {quickGenres.map(genre => (
          <button
            key={genre}
            className={`orama-genre-btn${(genre === 'All' && filters.genres.length === 0) || filters.genres.includes(genre) ? ' orama-genre-btn--active' : ''}`}
            onClick={() => handleQuickGenreClick(genre)}
          >
            {((genre === 'All' && filters.genres.length === 0) || (filters.genres.length === 1 && filters.genres[0] === genre)) && <span className="orama-genre-indicator">▶</span>}
            {genre}
          </button>
        ))}
      </div>
      <div className="orama-header__right">
        <button className="orama-search-btn" onClick={onSearchClick}>
          {Icons.search}
          <span>Search</span>
        </button>
        <div className="orama-avatar">JW</div>
      </div>
    </header>
  );
};

const Hero: React.FC<{ item: MediaItem | null; onPlay: () => void; onAddWatchlist: () => void }> = ({ item, onPlay, onAddWatchlist }) => {
  if (!item) return <div className="orama-hero orama-hero--loading"><div className="orama-spinner" /></div>;

  return (
    <div className="orama-hero">
      <div className="orama-hero__bg" style={{ backgroundImage: item.backdropUrl || item.imageUrl ? `url(${item.backdropUrl || item.imageUrl})` : undefined }} />
      <div className="orama-hero__content">
        <h1 className="orama-hero__title">{item.title}</h1>
        <div className="orama-hero__meta">
          {item.genres[0] && <span>{item.genres[0]}</span>}
          {item.year && <span> • {item.year}</span>}
          {item.rating && <span> • ★ {item.rating}</span>}
        </div>
        <p className="orama-hero__desc">{item.synopsis ? item.synopsis.slice(0, 180) + '...' : 'A gripping new series.'}</p>
        <div className="orama-hero__actions">
          <button className="orama-btn orama-btn--primary" onClick={onPlay}>{Icons.play} Watch Now</button>
          <button className="orama-btn orama-btn--secondary" onClick={onAddWatchlist}>{Icons.plus} Add to Watchlist</button>
        </div>
      </div>
    </div>
  );
};

const ContinueCard: React.FC<{ item: MediaItem; onClick: () => void }> = ({ item, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  // Use item progress from EMBY or calculate stable progress based on item ID
  const progress = item.progress || ((parseInt(item.id.replace(/\D/g, '').slice(-2) || '50', 10) % 80) + 15);
  // Generate stable values based on item ID hash
  const idNum = parseInt(item.id.replace(/\D/g, '').slice(-4) || '1234', 10);
  const season = ((idNum % 3) + 1);
  const minutes = (idNum % 120) + 10;
  const seconds = (idNum % 60);

  return (
    <div className="orama-continue-card" onClick={onClick}>
      <div className="orama-continue-card__poster">
        {item.backdropUrl || item.imageUrl ? (
          <img src={item.backdropUrl || item.imageUrl} alt={item.title} loading="lazy" onLoad={() => setLoaded(true)} className={loaded ? 'loaded' : ''} />
        ) : <div className="orama-card-placeholder">🎬</div>}
      </div>
      <div className="orama-continue-card__info">
        <div className="orama-continue-card__title-row">
          <span className="orama-continue-card__title">{item.title}</span>
          <span className="orama-continue-card__episode">{item.type === 'series' ? `S${season}` : ''}</span>
          <span className="orama-continue-card__time">{minutes}:{seconds.toString().padStart(2, '0')}</span>
        </div>
        <div className="orama-continue-card__progress"><div className="orama-continue-card__progress-bar" style={{ width: `${progress}%` }} /></div>
      </div>
    </div>
  );
};

const TrendingCard: React.FC<{ item: MediaItem; onClick: () => void }> = ({ item, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="orama-trending-card" onClick={onClick}>
      <div className="orama-trending-card__poster">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.title} loading="lazy" onLoad={() => setLoaded(true)} className={loaded ? 'loaded' : ''} />
        ) : <div className="orama-card-placeholder">🎬</div>}
        <div className="orama-trending-card__overlay"><div className="orama-trending-card__play">{Icons.play}</div></div>
      </div>
      <h3 className="orama-trending-card__title">{item.title}</h3>
      <div className="orama-trending-card__meta">
        {item.rating && <span className="orama-trending-card__rating" title="IMDB">★ {item.rating}</span>}
        {item.criticRating && <span className="orama-trending-card__critic" title="Rotten Tomatoes">🍅 {item.criticRating}%</span>}
        {item.genres[0] && <span className="orama-trending-card__genre">{item.genres[0]}</span>}
      </div>
    </div>
  );
};

const ContentSection: React.FC<{
  title: string;
  count?: number;
  items: MediaItem[];
  onItemClick: (item: MediaItem) => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
}> = ({ title, count, items, onItemClick, expanded, onToggleExpand }) => {
  const displayItems = expanded ? items : items.slice(0, 15);
  const hasMore = items.length > 15;

  const handleSeeAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`[ContentSection] "${title}" See All clicked, expanded=${expanded}, hasMore=${hasMore}, itemCount=${items.length}`);
    onToggleExpand?.();
  };

  return (
    <section className="orama-section">
      <div className="orama-section__header">
        <h2 className="orama-section__title">
          {title}
          <span className="orama-section__count"> ({items.length} items{expanded ? ' - EXPANDED' : ''})</span>
        </h2>
        {hasMore && (
          <button className="orama-section__more" onClick={handleSeeAll}>
            {expanded ? '← Show less' : `See all ${items.length} →`}
          </button>
        )}
      </div>
      <div className={`orama-row ${expanded ? 'orama-row--expanded' : ''}`}>
        {displayItems.map(item => <TrendingCard key={item.id} item={item} onClick={() => onItemClick(item)} />)}
      </div>
      {expanded && (
        <div className="orama-section__load-info">Showing all {items.length} items</div>
      )}
    </section>
  );
};

const SearchOverlay: React.FC<{ isOpen: boolean; onClose: () => void; results: MediaItem[]; onSearch: (q: string) => void; onItemClick: (i: MediaItem) => void }> = ({ isOpen, onClose, results, onSearch, onItemClick }) => {
  const [query, setQuery] = useState('');
  useEffect(() => { if (isOpen && query.length > 1) { const t = setTimeout(() => onSearch(query), 300); return () => clearTimeout(t); } }, [query, isOpen, onSearch]);
  if (!isOpen) return null;
  return (
    <div className="orama-search-overlay">
      <button className="orama-search-overlay__close" onClick={onClose}>{Icons.x}</button>
      <input type="text" className="orama-search-input" placeholder="Search movies, shows..." value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      <div className="orama-search-results">
        <div className="orama-search-grid">{results.map(item => <TrendingCard key={item.id} item={item} onClick={() => { onClose(); onItemClick(item); }} />)}</div>
        {query.length > 1 && results.length === 0 && <div className="orama-empty">No results for "{query}"</div>}
      </div>
    </div>
  );
};

const DetailModal: React.FC<{ item: MediaItem | null; onClose: () => void; onPlay: () => void; similarItems: MediaItem[] }> = ({ item, onClose, onPlay, similarItems }) => {
  const [enrichedItem, setEnrichedItem] = useState<MediaItem | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item) {
      setEnrichedItem(null);
      return;
    }
    // If item lacks metadata, fetch from TMDB
    if (!item.rating || !item.actors || item.actors.length === 0) {
      setLoading(true);
      enrichWithTMDB(item).then(enriched => {
        setEnrichedItem(enriched);
        setLoading(false);
      });
    } else {
      setEnrichedItem(item);
    }
  }, [item]);

  if (!item) return null;
  const displayItem = enrichedItem || item;

  return (
    <div className="orama-detail-modal" onClick={onClose}>
      <div className="orama-detail-modal__content" onClick={(e) => e.stopPropagation()}>
        <div className="orama-detail-modal__header">
          {(displayItem.backdropUrl || displayItem.imageUrl) && <img src={displayItem.backdropUrl || displayItem.imageUrl} alt={displayItem.title} />}
          <button className="orama-detail-modal__close" onClick={onClose}>{Icons.x}</button>
          {loading && <div className="orama-detail-modal__loading">Loading details...</div>}
        </div>
        <div className="orama-detail-modal__body">
          <h1 className="orama-detail-modal__title">{displayItem.title}</h1>
          <div className="orama-detail-modal__meta">
            {displayItem.rating && <span className="orama-detail-modal__rating" title="TMDB Rating">★ {displayItem.rating}</span>}
            {displayItem.criticRating && <span className="orama-detail-modal__critic" title="Rotten Tomatoes">🍅 {displayItem.criticRating}%</span>}
            {displayItem.officialRating && <span className="orama-detail-modal__mpaa">{displayItem.officialRating}</span>}
            {displayItem.year && <span>{displayItem.year}</span>}
            {displayItem.runtime && <span>{displayItem.runtime}</span>}
            {displayItem.genres.slice(0, 2).map(g => <span key={g} className="orama-detail-modal__genre">{g}</span>)}
          </div>
          {displayItem.tagline && <p className="orama-detail-modal__tagline">"{displayItem.tagline}"</p>}
          <p className="orama-detail-modal__desc">{displayItem.synopsis || 'No description available.'}</p>

          {/* Cast & Crew */}
          <div className="orama-detail-modal__credits">
            {displayItem.director && <p><strong>Director:</strong> {displayItem.director}</p>}
            {displayItem.actors && displayItem.actors.length > 0 && <p><strong>Cast:</strong> {displayItem.actors.join(', ')}</p>}
            {displayItem.studio && <p><strong>Studio:</strong> {displayItem.studio}</p>}
          </div>

          <div className="orama-detail-modal__actions">
            <button className="orama-btn orama-btn--primary" onClick={onPlay}>{Icons.play} Play</button>
            <button className="orama-btn orama-btn--secondary">{Icons.plus} Watchlist</button>
            {displayItem.tmdbId && (
              <a href={`https://www.themoviedb.org/movie/${displayItem.tmdbId}`} target="_blank" rel="noopener noreferrer" className="orama-btn orama-btn--secondary">
                TMDB ↗
              </a>
            )}
            {displayItem.imdbId && (
              <a href={`https://www.imdb.com/title/${displayItem.imdbId}`} target="_blank" rel="noopener noreferrer" className="orama-btn orama-btn--secondary">
                IMDB ↗
              </a>
            )}
          </div>
          {similarItems.length > 0 && <>
            <h3 className="orama-detail-modal__section-title">More Like This</h3>
            <div className="orama-row">{similarItems.slice(0, 6).map(s => <TrendingCard key={s.id} item={s} onClick={() => {}} />)}</div>
          </>}
        </div>
      </div>
    </div>
  );
};

// Advanced Video Player Icons
const PlayerIcons = {
  play: <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  pause: <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  volumeHigh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
  volumeLow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
  volumeMute: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>,
  fullscreen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>,
  exitFullscreen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>,
  skipBack: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 19 2 12 11 5 11 19"/><polygon points="22 19 13 12 22 5 22 19"/></svg>,
  skipForward: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 19 22 12 13 5 13 19"/><polygon points="2 19 11 12 2 5 2 19"/></svg>,
  settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  pip: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor"/></svg>,
  subtitles: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="6" y1="16" x2="14" y2="16"/></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>,
};

// Format time helper
const formatPlayerTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Quality options for Emby streaming
const QUALITY_OPTIONS = [
  { label: 'Auto', bitrate: 0 },
  { label: '4K', bitrate: 120000000 },
  { label: '1080p', bitrate: 20000000 },
  { label: '720p', bitrate: 8000000 },
  { label: '480p', bitrate: 3000000 },
  { label: '360p', bitrate: 1500000 },
];

const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

interface SubtitleTrack {
  index: number;
  language: string;
  displayTitle: string;
}

const VideoPlayer: React.FC<{ item: MediaItem | null; onClose: () => void }> = ({ item, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const hideControlsTimer = useRef<NodeJS.Timeout | null>(null);

  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [quality, setQuality] = useState(QUALITY_OPTIONS[0]);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'main' | 'quality' | 'speed' | 'subtitles'>('main');
  const [subtitles, setSubtitles] = useState<SubtitleTrack[]>([]);
  const [activeSubtitle, setActiveSubtitle] = useState<number>(-1);
  const [isPiP, setIsPiP] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Store the actual MediaSourceId from PlaybackInfo
  const [mediaSourceId, setMediaSourceId] = useState<string | null>(null);

  // Build stream URL - EMBY requires the actual MediaSourceId from PlaybackInfo
  const getStreamUrl = useCallback(() => {
    if (!item) return '';
    // Simple direct stream URL that works with most EMBY setups
    // Using the universal endpoint without specific container
    return `${EMBY_CONFIG.serverUrl}/emby/Videos/${item.id}/stream?Static=true&MediaSourceId=${mediaSourceId || item.id}&api_key=${EMBY_CONFIG.apiKey}`;
  }, [item, mediaSourceId]);

  // Fetch PlaybackInfo for MediaSourceId and subtitles
  useEffect(() => {
    if (!item) return;
    fetch(`${EMBY_CONFIG.serverUrl}/emby/Items/${item.id}/PlaybackInfo?api_key=${EMBY_CONFIG.apiKey}`)
      .then(res => res.json())
      .then(data => {
        const mediaSource = data.MediaSources?.[0];
        if (mediaSource) {
          // Set the actual MediaSourceId from PlaybackInfo
          setMediaSourceId(mediaSource.Id);
          console.log('[VideoPlayer] MediaSource:', mediaSource.Id, mediaSource.Path);

          // Get subtitles
          if (mediaSource.MediaStreams) {
            const subs = mediaSource.MediaStreams
              .filter((s: any) => s.Type === 'Subtitle')
              .map((s: any) => ({
                index: s.Index,
                language: s.Language || 'Unknown',
                displayTitle: s.DisplayTitle || s.Language || 'Subtitle',
              }));
            setSubtitles(subs);
          }
        }
      })
      .catch(err => console.error('[VideoPlayer] PlaybackInfo error:', err));
  }, [item]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!videoRef.current) return;

      switch (e.key.toLowerCase()) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'arrowleft':
          e.preventDefault();
          skip(-10);
          break;
        case 'arrowright':
          e.preventDefault();
          skip(10);
          break;
        case 'arrowup':
          e.preventDefault();
          adjustVolume(0.1);
          break;
        case 'arrowdown':
          e.preventDefault();
          adjustVolume(-0.1);
          break;
        case 'escape':
          if (isFullscreen) {
            document.exitFullscreen();
          } else {
            onClose();
          }
          break;
        case 'p':
          e.preventDefault();
          togglePiP();
          break;
        case 'c':
          e.preventDefault();
          cycleSubtitles();
          break;
        case ',':
          e.preventDefault();
          changeSpeed(-1);
          break;
        case '.':
          e.preventDefault();
          changeSpeed(1);
          break;
        case '0': case '1': case '2': case '3': case '4':
        case '5': case '6': case '7': case '8': case '9':
          e.preventDefault();
          const percent = parseInt(e.key) * 10;
          videoRef.current.currentTime = (duration * percent) / 100;
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [duration, isFullscreen, subtitles, activeSubtitle, playbackSpeed]);

  // Auto-hide controls
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    if (isPlaying) {
      hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying]);

  // Player control functions
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const adjustVolume = (delta: number) => {
    const newVol = Math.max(0, Math.min(1, volume + delta));
    setVolume(newVol);
    if (videoRef.current) videoRef.current.volume = newVol;
    setIsMuted(newVol === 0);
  };

  const skip = (seconds: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      await containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePiP = async () => {
    if (!videoRef.current) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiP(false);
      } else {
        await videoRef.current.requestPictureInPicture();
        setIsPiP(true);
      }
    } catch (err) {
      console.error('PiP error:', err);
    }
  };

  const cycleSubtitles = () => {
    if (subtitles.length === 0) return;
    const nextIndex = activeSubtitle === -1 ? 0 : (activeSubtitle + 1 >= subtitles.length ? -1 : activeSubtitle + 1);
    setActiveSubtitle(nextIndex);
  };

  const changeSpeed = (direction: number) => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const newIndex = Math.max(0, Math.min(PLAYBACK_SPEEDS.length - 1, currentIndex + direction));
    setPlaybackSpeed(PLAYBACK_SPEEDS[newIndex]);
    if (videoRef.current) videoRef.current.playbackRate = PLAYBACK_SPEEDS[newIndex];
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !videoRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = duration * percent;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVol = parseFloat(e.target.value);
    setVolume(newVol);
    if (videoRef.current) videoRef.current.volume = newVol;
    setIsMuted(newVol === 0);
  };

  // Video event handlers
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    if (videoRef.current.buffered.length > 0) {
      setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
    }
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
    setIsLoading(false);
  };

  if (!item) return null;

  return (
    <div
      ref={containerRef}
      className={`video-player ${showControls ? 'video-player--controls-visible' : ''}`}
      onMouseMove={resetHideTimer}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className="video-player__video"
        src={getStreamUrl()}
        autoPlay
        onClick={togglePlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
        onError={(e) => setError('Failed to load video')}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div className="video-player__loading">
          <div className="video-player__spinner" />
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="video-player__error">
          <p>{error}</p>
          <button onClick={() => { setError(null); videoRef.current?.load(); }}>Retry</button>
        </div>
      )}

      {/* Top Bar */}
      <div className="video-player__top">
        <button className="video-player__btn" onClick={onClose} title="Close (Esc)">
          {PlayerIcons.back}
        </button>
        <div className="video-player__title">
          <h2>{item.title}</h2>
          {item.year && <span>{item.year}</span>}
        </div>
      </div>

      {/* Center Play Button */}
      <button className="video-player__center-play" onClick={togglePlay}>
        {isPlaying ? PlayerIcons.pause : PlayerIcons.play}
      </button>

      {/* Bottom Controls */}
      <div className="video-player__bottom">
        {/* Progress Bar */}
        <div className="video-player__progress" ref={progressRef} onClick={handleSeek}>
          <div className="video-player__progress-buffered" style={{ width: `${(buffered / duration) * 100}%` }} />
          <div className="video-player__progress-played" style={{ width: `${(currentTime / duration) * 100}%` }} />
          <div className="video-player__progress-thumb" style={{ left: `${(currentTime / duration) * 100}%` }} />
        </div>

        {/* Controls Row */}
        <div className="video-player__controls">
          {/* Left Controls */}
          <div className="video-player__controls-left">
            <button className="video-player__btn" onClick={togglePlay} title={isPlaying ? 'Pause (K)' : 'Play (K)'}>
              {isPlaying ? PlayerIcons.pause : PlayerIcons.play}
            </button>
            <button className="video-player__btn" onClick={() => skip(-10)} title="Rewind 10s (←)">
              {PlayerIcons.skipBack}
            </button>
            <button className="video-player__btn" onClick={() => skip(10)} title="Forward 10s (→)">
              {PlayerIcons.skipForward}
            </button>

            {/* Volume */}
            <div className="video-player__volume">
              <button className="video-player__btn" onClick={toggleMute} title={isMuted ? 'Unmute (M)' : 'Mute (M)'}>
                {isMuted || volume === 0 ? PlayerIcons.volumeMute : volume < 0.5 ? PlayerIcons.volumeLow : PlayerIcons.volumeHigh}
              </button>
              <input
                type="range"
                className="video-player__volume-slider"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
              />
            </div>

            {/* Time Display */}
            <span className="video-player__time">
              {formatPlayerTime(currentTime)} / {formatPlayerTime(duration)}
            </span>
          </div>

          {/* Right Controls */}
          <div className="video-player__controls-right">
            {/* Speed Indicator */}
            {playbackSpeed !== 1 && (
              <span className="video-player__speed-indicator">{playbackSpeed}x</span>
            )}

            {/* Subtitles */}
            {subtitles.length > 0 && (
              <button
                className={`video-player__btn ${activeSubtitle >= 0 ? 'video-player__btn--active' : ''}`}
                onClick={cycleSubtitles}
                title="Subtitles (C)"
              >
                {PlayerIcons.subtitles}
              </button>
            )}

            {/* Settings */}
            <div className="video-player__settings-container">
              <button
                className="video-player__btn"
                onClick={() => setShowSettings(!showSettings)}
                title="Settings"
              >
                {PlayerIcons.settings}
              </button>

              {showSettings && (
                <div className="video-player__settings-menu">
                  {settingsTab === 'main' && (
                    <>
                      <button onClick={() => setSettingsTab('speed')}>
                        <span>Playback Speed</span>
                        <span>{playbackSpeed}x</span>
                      </button>
                      <button onClick={() => setSettingsTab('quality')}>
                        <span>Quality</span>
                        <span>{quality.label}</span>
                      </button>
                      {subtitles.length > 0 && (
                        <button onClick={() => setSettingsTab('subtitles')}>
                          <span>Subtitles</span>
                          <span>{activeSubtitle >= 0 ? subtitles[activeSubtitle]?.displayTitle : 'Off'}</span>
                        </button>
                      )}
                    </>
                  )}
                  {settingsTab === 'speed' && (
                    <>
                      <button className="video-player__settings-back" onClick={() => setSettingsTab('main')}>
                        {PlayerIcons.back} Playback Speed
                      </button>
                      {PLAYBACK_SPEEDS.map(speed => (
                        <button
                          key={speed}
                          className={playbackSpeed === speed ? 'active' : ''}
                          onClick={() => {
                            setPlaybackSpeed(speed);
                            if (videoRef.current) videoRef.current.playbackRate = speed;
                            setShowSettings(false);
                            setSettingsTab('main');
                          }}
                        >
                          {speed}x {speed === 1 && '(Normal)'}
                        </button>
                      ))}
                    </>
                  )}
                  {settingsTab === 'quality' && (
                    <>
                      <button className="video-player__settings-back" onClick={() => setSettingsTab('main')}>
                        {PlayerIcons.back} Quality
                      </button>
                      {QUALITY_OPTIONS.map(q => (
                        <button
                          key={q.label}
                          className={quality.label === q.label ? 'active' : ''}
                          onClick={() => {
                            setQuality(q);
                            setShowSettings(false);
                            setSettingsTab('main');
                          }}
                        >
                          {q.label}
                        </button>
                      ))}
                    </>
                  )}
                  {settingsTab === 'subtitles' && (
                    <>
                      <button className="video-player__settings-back" onClick={() => setSettingsTab('main')}>
                        {PlayerIcons.back} Subtitles
                      </button>
                      <button
                        className={activeSubtitle === -1 ? 'active' : ''}
                        onClick={() => { setActiveSubtitle(-1); setShowSettings(false); setSettingsTab('main'); }}
                      >
                        Off
                      </button>
                      {subtitles.map(sub => (
                        <button
                          key={sub.index}
                          className={activeSubtitle === sub.index ? 'active' : ''}
                          onClick={() => { setActiveSubtitle(sub.index); setShowSettings(false); setSettingsTab('main'); }}
                        >
                          {sub.displayTitle}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Picture in Picture */}
            {document.pictureInPictureEnabled && (
              <button
                className={`video-player__btn ${isPiP ? 'video-player__btn--active' : ''}`}
                onClick={togglePiP}
                title="Picture in Picture (P)"
              >
                {PlayerIcons.pip}
              </button>
            )}

            {/* Fullscreen */}
            <button className="video-player__btn" onClick={toggleFullscreen} title="Fullscreen (F)">
              {isFullscreen ? PlayerIcons.exitFullscreen : PlayerIcons.fullscreen}
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="video-player__shortcuts-hint">
        <span>Space/K: Play/Pause</span>
        <span>←/→: Skip 10s</span>
        <span>↑/↓: Volume</span>
        <span>F: Fullscreen</span>
        <span>M: Mute</span>
        <span>C: Subtitles</span>
        <span>,/.: Speed</span>
      </div>
    </div>
  );
};

const MobileNav: React.FC<{ activeNav: string; onNavChange: (id: string) => void; onSearchClick: () => void }> = ({ activeNav, onNavChange, onSearchClick }) => (
  <nav className="orama-mobile-nav">
    {[{ id: 'home', label: 'Home', icon: Icons.home }, { id: 'discover', label: 'Discover', icon: Icons.discover }, { id: 'watchlist', label: 'Watchlist', icon: Icons.watchlist }].map(item => (
      <button key={item.id} className={`orama-mobile-nav__item${activeNav === item.id ? ' orama-mobile-nav__item--active' : ''}`} onClick={() => onNavChange(item.id)}>{item.icon}<span>{item.label}</span></button>
    ))}
    <button className="orama-mobile-nav__item" onClick={onSearchClick}>{Icons.search}<span>Search</span></button>
  </nav>
);

// ========== MAIN ==========
export default function MediaPage() {
  const [activeNav, setActiveNav] = useState('home');
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<MediaItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [playingItem, setPlayingItem] = useState<MediaItem | null>(null);
  const [similarItems, setSimilarItems] = useState<MediaItem[]>([]);
  const [featuredItem, setFeaturedItem] = useState<MediaItem | null>(null);
  const [allMovies, setAllMovies] = useState<MediaItem[]>([]);
  const [allShows, setAllShows] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Extract all unique actors from the library
  const allActors = useMemo(() => {
    const actorSet = new Set<string>();
    [...allMovies, ...allShows].forEach(item => {
      item.actors?.forEach(a => actorSet.add(a));
    });
    return Array.from(actorSet).sort();
  }, [allMovies, allShows]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.genres.length > 0) count++;
    if (filters.duration !== DURATION_FILTERS[0]) count++;
    if (filters.year !== YEAR_FILTERS[0]) count++;
    if (filters.rating !== RATING_FILTERS[0]) count++;
    if (filters.contentType !== 'all') count++;
    if (filters.minRating > 0) count++;
    if (filters.actors.length > 0) count++;
    if (filters.sort !== SORT_OPTIONS[0]) count++;
    return count;
  }, [filters]);

  const toggleSection = (section: string) => {
    console.log(`[MediaPage] Toggling section "${section}"`, { current: expandedSections[section], newValue: !expandedSections[section] });
    setExpandedSections(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      console.log(`[MediaPage] Section state updated:`, newState);
      return newState;
    });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const fields = 'Overview,Genres,CommunityRating,CriticRating,People,Studios,ProviderIds,OfficialRating,Taglines,RunTimeTicks';
        const [moviesRes, showsRes] = await Promise.all([
          fetch(`${EMBY_CONFIG.serverUrl}/emby/Items?IncludeItemTypes=Movie&Recursive=true&SortBy=DateCreated&SortOrder=Descending&Fields=${fields}&Limit=500&api_key=${EMBY_CONFIG.apiKey}`),
          fetch(`${EMBY_CONFIG.serverUrl}/emby/Items?IncludeItemTypes=Series&Recursive=true&SortBy=DateCreated&SortOrder=Descending&Fields=${fields}&Limit=500&api_key=${EMBY_CONFIG.apiKey}`)
        ]);
        if (moviesRes.ok) {
          const data = await moviesRes.json();
          console.log(`[MediaPage] Loaded ${data.Items?.length || 0} movies of ${data.TotalRecordCount} total`);
          const movies = (data.Items || []).map(embyToMediaItem);
          setAllMovies(movies);
          const featured = movies.find((m: MediaItem) => m.backdropUrl) || movies[0];
          if (featured) setFeaturedItem(featured);
        }
        if (showsRes.ok) {
          const data = await showsRes.json();
          console.log(`[MediaPage] Loaded ${data.Items?.length || 0} TV shows of ${data.TotalRecordCount} total`);
          setAllShows((data.Items || []).map(embyToMediaItem));
        }
      } catch (err) { console.error('Fetch error:', err); }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Comprehensive filtering function
  const applyFilters = useCallback((items: MediaItem[]): MediaItem[] => {
    let result = [...items];

    // Content type filter
    if (filters.contentType !== 'all') {
      result = result.filter(i => i.type === filters.contentType);
    }

    // Genre filter (match any selected genre)
    if (filters.genres.length > 0) {
      result = result.filter(i =>
        i.genres.some(g =>
          filters.genres.some(fg =>
            g.toLowerCase().includes(fg.toLowerCase()) ||
            fg.toLowerCase().includes(g.toLowerCase())
          )
        )
      );
    }

    // Duration filter
    if (filters.duration !== DURATION_FILTERS[0]) {
      result = result.filter(i => {
        const mins = getRuntimeMinutes(i.runtime);
        if (mins === 0) return true; // Include items without runtime data
        return mins >= filters.duration.min && mins < filters.duration.max;
      });
    }

    // Year filter
    if (filters.year !== YEAR_FILTERS[0]) {
      result = result.filter(i => {
        if (!i.year) return true;
        return i.year >= filters.year.min && i.year <= filters.year.max;
      });
    }

    // MPAA Rating filter
    if (filters.rating !== RATING_FILTERS[0] && filters.rating.values.length > 0) {
      result = result.filter(i => {
        if (!i.officialRating) return filters.rating.values.includes('');
        return filters.rating.values.some(r =>
          i.officialRating?.toLowerCase() === r.toLowerCase()
        );
      });
    }

    // Minimum score filter
    if (filters.minRating > 0) {
      result = result.filter(i => (i.rating || 0) >= filters.minRating);
    }

    // Actor filter (match all selected actors)
    if (filters.actors.length > 0) {
      result = result.filter(i =>
        filters.actors.every(actor =>
          i.actors?.some(a => a.toLowerCase().includes(actor.toLowerCase()))
        )
      );
    }

    // Sort
    result.sort((a, b) => {
      const { key, order } = filters.sort;
      let cmp = 0;
      switch (key) {
        case 'title':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'year':
          cmp = (a.year || 0) - (b.year || 0);
          break;
        case 'rating':
          cmp = (a.rating || 0) - (b.rating || 0);
          break;
        default:
          cmp = 0;
      }
      return order === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [filters]);

  // Get ALL top rated items (no limit) - sorted by rating descending
  const getTopRated = useCallback((items: MediaItem[]) => [...items].filter(i => i.rating).sort((a, b) => (b.rating || 0) - (a.rating || 0)), []);

  const handleSearch = useCallback(async (query: string) => {
    try {
      const res = await fetch(`${EMBY_CONFIG.serverUrl}/emby/Items?SearchTerm=${encodeURIComponent(query)}&Limit=30&Recursive=true&IncludeItemTypes=Movie,Series&Fields=Overview,Genres,CommunityRating,CriticRating,People,Studios&api_key=${EMBY_CONFIG.apiKey}`);
      if (res.ok) { const data = await res.json(); setSearchResults((data.Items || []).map(embyToMediaItem)); }
    } catch (err) { console.error('Search error:', err); }
  }, []);

  useEffect(() => {
    if (!selectedItem) { setSimilarItems([]); return; }
    const genre = selectedItem.genres[0];
    if (genre) setSimilarItems([...allMovies, ...allShows].filter(i => i.id !== selectedItem.id && i.genres.includes(genre)).slice(0, 8));
  }, [selectedItem, allMovies, allShows]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
      if (e.key === 'Escape') { setSearchOpen(false); setSelectedItem(null); setPlayingItem(null); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Apply filters to content
  const filteredMovies = useMemo(() => applyFilters(allMovies), [allMovies, applyFilters]);
  const filteredShows = useMemo(() => applyFilters(allShows), [allShows, applyFilters]);
  const allFiltered = useMemo(() => applyFilters([...allMovies, ...allShows]), [allMovies, allShows, applyFilters]);
  const continueWatching = [...allMovies, ...allShows].slice(0, 8);
  const topRated = getTopRated(allFiltered);

  // Handle filter reset
  const handleResetFilters = () => setFilters(defaultFilters);

  if (loading) return <div className="orama-app"><Sidebar activeNav={activeNav} onNavChange={setActiveNav} /><main className="orama-main"><div className="orama-loading"><div className="orama-spinner" /><p>Loading library...</p></div></main></div>;

  return (
    <div className="orama-app">
      <Sidebar activeNav={activeNav} onNavChange={setActiveNav} />
      <main className="orama-main">
        <Header
          filters={filters}
          onFiltersChange={setFilters}
          onSearchClick={() => setSearchOpen(true)}
          allActors={allActors}
          onResetFilters={handleResetFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {/* Advanced Filter Panel */}
        <FilterPanel
          filters={filters}
          onFiltersChange={setFilters}
          allActors={allActors}
          onReset={handleResetFilters}
          activeFiltersCount={activeFiltersCount}
        />

        <Hero item={featuredItem} onPlay={() => featuredItem && setPlayingItem(featuredItem)} onAddWatchlist={() => {}} />

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && (
          <div className="orama-active-filters">
            <span className="orama-active-filters__label">Active Filters:</span>
            {filters.genres.length > 0 && <span className="orama-active-filters__tag">Genres: {filters.genres.join(', ')}</span>}
            {filters.contentType !== 'all' && <span className="orama-active-filters__tag">Type: {filters.contentType === 'movie' ? 'Movies' : 'TV Shows'}</span>}
            {filters.duration !== DURATION_FILTERS[0] && <span className="orama-active-filters__tag">Duration: {filters.duration.label}</span>}
            {filters.year !== YEAR_FILTERS[0] && <span className="orama-active-filters__tag">Year: {filters.year.label}</span>}
            {filters.rating !== RATING_FILTERS[0] && <span className="orama-active-filters__tag">Rating: {filters.rating.label}</span>}
            {filters.minRating > 0 && <span className="orama-active-filters__tag">Min Score: {filters.minRating}+</span>}
            {filters.actors.length > 0 && <span className="orama-active-filters__tag">Actors: {filters.actors.join(', ')}</span>}
            <span className="orama-active-filters__results">{allFiltered.length} results</span>
          </div>
        )}

        {/* Continue Watching - keeps original format */}
        <section className="orama-section">
          <div className="orama-section__header">
            <h2 className="orama-section__title">Continue Watching<span className="orama-section__count"> ({continueWatching.length})</span></h2>
          </div>
          <div className="orama-row">{continueWatching.map(item => <ContinueCard key={item.id} item={item} onClick={() => setSelectedItem(item)} />)}</div>
        </section>

        {/* Show "All Results" when filters are active */}
        {activeFiltersCount > 0 ? (
          <ContentSection
            title="Filtered Results"
            count={allFiltered.length}
            items={allFiltered}
            onItemClick={setSelectedItem}
            expanded={expandedSections['filtered']}
            onToggleExpand={() => toggleSection('filtered')}
          />
        ) : (
          <>
            <ContentSection title="Trending Now" count={topRated.length} items={topRated} onItemClick={setSelectedItem} expanded={expandedSections['trending']} onToggleExpand={() => toggleSection('trending')} />
            <ContentSection title="Movies" count={filteredMovies.length} items={filteredMovies} onItemClick={setSelectedItem} expanded={expandedSections['movies']} onToggleExpand={() => toggleSection('movies')} />
            <ContentSection title="TV Shows" count={filteredShows.length} items={filteredShows} onItemClick={setSelectedItem} expanded={expandedSections['shows']} onToggleExpand={() => toggleSection('shows')} />
          </>
        )}
      </main>
      <MobileNav activeNav={activeNav} onNavChange={setActiveNav} onSearchClick={() => setSearchOpen(true)} />
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} results={searchResults} onSearch={handleSearch} onItemClick={setSelectedItem} />
      <DetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onPlay={() => { if (selectedItem) { setPlayingItem(selectedItem); setSelectedItem(null); }}} similarItems={similarItems} />
      <VideoPlayer item={playingItem} onClose={() => setPlayingItem(null)} />
    </div>
  );
}
